import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  allowedDocumentFileSize,
  allowedImageSizeLabel
} from '@constants/file';
import { bytesToMB } from '@utils/file';

import { UploadStatus } from '../constants/leadFileUpload';
import { useGetUploadUrlMutation } from '../queries/LeadManagementQuery';
import { SelectedFileItem } from '../types/leadFileUpload';

type UseLeadFileUploadParams = {
  organizationId: string;
  fileType?: string;
  onError?: (message: string) => void;
  onUploaded?: (file: File, objectKey: string) => void;
  onBatchCompleted?: (
    completedItems: SelectedFileItem[],
    batchId: string
  ) => void;
};

type UseLeadFileUploadReturn = {
  items: SelectedFileItem[];
  addFiles: (files: File[] | FileList) => void;
  removeItem: (id: string) => void;
  reset: () => void;
  setItems: (items: SelectedFileItem[]) => void;
  isGettingUploadUrl: boolean;
};

export const useLeadFileUpload = (
  params: UseLeadFileUploadParams
): UseLeadFileUploadReturn => {
  const {
    organizationId,
    fileType = 'application/pdf',
    onError,
    onUploaded,
    onBatchCompleted
  } = params;

  const [getUploadUrl, { isLoading: isGettingUploadUrl }] =
    useGetUploadUrlMutation();

  const [items, setItems] = useState<SelectedFileItem[]>([]);

  const notifiedBatchIdsRef = useRef<Set<string>>(new Set());

  const validateFile = useCallback(
    (file: File) => {
      if (file.size > allowedDocumentFileSize) {
        const errorMessage = `Upload failed. File size more than ${allowedImageSizeLabel}`;
        onError?.(errorMessage);
        return false;
      }
      return true;
    },
    [onError]
  );

  const uploadToPresignedUrl = useCallback(async (url: string, file: File) => {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          Origin: window.location.origin,
          Referer: window.location.href
        }
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  }, []);

  const startUpload = useCallback(
    async (tempId: string, file: File) => {
      try {
        const res: any = await getUploadUrl({
          organizationId,
          fileType: file.type || fileType
        });

        if (!('data' in res)) {
          throw new Error('Failed to get upload URL');
        }

        const uploadUrl: string = res.data.url;
        const objectKey: string = res.data.object_key;

        const ok = await uploadToPresignedUrl(uploadUrl, file);
        if (!ok) throw new Error('Upload failed');

        setItems((prev) =>
          prev.map((it) =>
            it.id === tempId
              ? {
                  ...it,
                  objectKey,
                  status: UploadStatus.COMPLETED
                }
              : it
          )
        );

        onUploaded?.(file, objectKey);
      } catch (e) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === tempId
              ? {
                  ...it,
                  status: UploadStatus.FAILED,
                  errorMessage: (e as Error)?.message || 'Upload failed'
                }
              : it
          )
        );
      }
    },
    [fileType, getUploadUrl, onUploaded, organizationId, uploadToPresignedUrl]
  );

  const addFiles = useCallback(
    (filesList: File[] | FileList) => {
      const files = Array.from(filesList);
      const validFiles = files.filter(validateFile);

      const batchId = `batch_${Date.now()}`;

      const newItems: SelectedFileItem[] = validFiles.map((file) => {
        const tempId = `temp_${Date.now()}_${file.name}`;
        // Kick off upload in background per file
        startUpload(tempId, file);
        return {
          id: tempId,
          name: file.name,
          sizeLabel: bytesToMB(file.size),
          status: UploadStatus.IN_PROGRESS,
          sizeBytes: file.size,
          batchId
        };
      });

      if (newItems.length > 0) {
        setItems((prev) => [...prev, ...newItems]);
      }
    },
    [startUpload, validateFile]
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const reset = useCallback(() => setItems([]), []);

  // Notify when a batch completes (no files in the batch are in progress)
  useEffect(() => {
    if (!onBatchCompleted) return;

    const batches: Record<string, SelectedFileItem[]> = {};
    items.forEach((it) => {
      if (!it.batchId) return;
      if (!batches[it.batchId]) batches[it.batchId] = [];
      batches[it.batchId].push(it);
    });

    Object.entries(batches).forEach(([bId, bItems]) => {
      if (notifiedBatchIdsRef.current.has(bId)) return;
      const anyInProgress = bItems.some(
        (it) => it.status === UploadStatus.IN_PROGRESS
      );
      if (!anyInProgress) {
        const completed = bItems.filter(
          (it) => it.status === UploadStatus.COMPLETED && it.objectKey
        );
        onBatchCompleted(completed, bId);
        notifiedBatchIdsRef.current.add(bId);
      }
    });
  }, [items, onBatchCompleted]);

  return useMemo(
    () => ({
      items,
      addFiles,
      removeItem,
      reset,
      setItems,
      isGettingUploadUrl
    }),
    [addFiles, items, isGettingUploadUrl, removeItem, reset, setItems]
  );
};

export default useLeadFileUpload;
